'use client';

import classNames from 'classnames';
import { useEffect, useMemo, useRef, useState } from 'react';
import Logo from '@/components/atoms/logo/logo';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import { TabProps } from '@/components/molecules/tab/tab';
import ContentSearchBanner from '@/components/organisms/content-search-banner/content-search-banner';
import ContentSearchBar from '@/components/organisms/content-search-bar/content-search-bar';
import ContentSearchCard from '@/components/organisms/content-search-card/content-search-card';
import TabMenu from '@/components/organisms/tab-menu/tab-menu';
import {
  getTabs,
  getTagNameMap,
} from '@/components/templates/content-search/content-search.utils';
import { useQueryContentSearch } from '@/integrations/content-search/content-search.hooks';
import {
  trackPageLoadEvent,
  trackViewEvent,
} from '@/integrations/tracking/google-tag-manager/scripts';
import { tealiumView } from '@/integrations/tracking/tealium/scripts';
import { useContentSearchContext } from '@/libraries/contexts/content-search-context/content-search-context';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { getLogoName } from '@/libraries/getters/get-logo-name';
import useIsScrollCompleted from '@/libraries/hooks/use-is-scroll-completed';
import useResizeObserver from '@/libraries/hooks/use-resize-observer';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import styles from './content-search.module.scss';

export interface ContentSearchProps {
  /** The type of the page */
  $type: 'ContentSearchPageDto';
  /** The class name to apply */
  className?: string;
  /** Personalization data for the user */
  personalizationData?: Content.Properties.PersonalizationData;
  /** Tracking data for the page */
  trackingData?: Content.Properties.TrackingData;
}

const ContentSearch = ({
  className,
  personalizationData,
  trackingData,
}: ContentSearchProps) => {
  const { t } = useI18n();
  const isLaptop = useWindowSize('laptop');
  const searchRef = useRef<HTMLDivElement>(null);
  const searchOffset = useResizeObserver(searchRef);

  const [activeTab, setActiveTab] = useState('');
  const [searchString, setSearchString] = useState('');
  const [navigationOffset, setNavigationOffset] = useState(0);
  const [scrollTarget, setScrollTarget] = useState<number | null>(null);

  const {
    contentSearchInput,
    contentSearchSettings,
    recentContentSearches,
    addContentSearchInput,
  } = useContentSearchContext();
  const { brand, handleUpdateIsScrolled, isScrolled, stickyTopContentHeight } =
    usePageDetailsContext();

  useIsScrollCompleted({
    isEnabled: !!scrollTarget,
    onComplete: () => {
      handleUpdateIsScrolled(true);
      setScrollTarget(null);
    },
    targetScrollY: scrollTarget ?? 0,
    threshold: 2,
  });

  const contentSearchTags = contentSearchSettings?.data?.contentSearchTags;

  const isContentSearchEnabled =
    !!searchString && !!contentSearchSettings?.parameters;

  const { data: dataContentSearch, isFetching: isFetchingContentSearch } =
    useQueryContentSearch(
      {
        ...contentSearchSettings?.parameters,
        brand,
        searchString,
      },
      { headers: contentSearchSettings?.headers },
      {
        enabled: isContentSearchEnabled,
      },
    );

  const scrollToElement = (id: string) => {
    if (!window) return;

    const targetElement = document.getElementById(id);

    if (!targetElement) return;

    const currentScroll = Math.round(window.scrollY);

    const offset = navigationOffset + 1;

    const direction =
      offset > currentScroll ? 'down' : offset < currentScroll ? 'up' : 'none';

    if (direction === 'up') {
      setScrollTarget(offset);
      handleUpdateIsScrolled(false);
    }

    window.scrollTo({ behavior: 'smooth', top: offset });
  };

  const hasResults =
    dataContentSearch &&
    dataContentSearch.items &&
    dataContentSearch.items.length > 0;

  const hasBanners =
    dataContentSearch &&
    dataContentSearch.banners &&
    dataContentSearch.banners.length > 0;

  const showResults = !isFetchingContentSearch && hasResults;

  const hasNoResults = !isFetchingContentSearch && !hasResults;

  const tabs: TabProps[] = useMemo(() => {
    if (!hasResults) return [];

    return getTabs(
      dataContentSearch.items,
      contentSearchTags,
      tag => {
        scrollToElement('results');
        setActiveTab(tag);
      },
      t,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentSearchSettings, dataContentSearch]);

  const hasTabs = !isFetchingContentSearch && tabs && tabs.length > 0;

  const filteredItems = useMemo(() => {
    if (!hasResults) return [];

    if (!activeTab) return dataContentSearch.items;

    return dataContentSearch.items.filter(item => item.tag === activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, dataContentSearch]);

  const formattedItems = useMemo(() => {
    return filteredItems.map(item => {
      if (!item.tag) return item;

      return {
        ...item,
        tag: getTagNameMap(contentSearchTags, 'resultValue')[item.tag],
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredItems]);

  useEffect(() => {
    trackPageLoadEvent({ content_group: 'content_search' });

    personalizationData && tealiumView(personalizationData);

    if (!trackingData?.gtmDataLayer) return;

    trackViewEvent(trackingData.gtmDataLayer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearchString(contentSearchInput);
  }, [contentSearchInput]);

  useEffect(() => {
    if (stickyTopContentHeight === 0) return;

    setNavigationOffset(stickyTopContentHeight);
  }, [stickyTopContentHeight, isLaptop]);

  useEffect(() => {
    if (!recentContentSearches[0]) return;

    setSearchString(recentContentSearches[0]);
    addContentSearchInput(recentContentSearches[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderNoResults = () => {
    return (
      <div className={styles.noResultsContainer}>
        <Logo
          className={styles.noResultsIcon}
          name={getLogoName({
            brand: brand,
            type: 'NoResults',
          })}
        />
        <div className={styles.noResultsContent}>
          <Typography
            className={styles.noResultsHeading}
            tag="h2"
            tagStyle="headingMedium"
          >
            {t('items.contentSearch.noResults.header')}
          </Typography>

          <Typography
            className={styles.noResultsText}
            tag="span"
            tagStyle="bodyMedium"
          >
            {t('items.contentSearch.noResults.text')}
          </Typography>
        </div>
      </div>
    );
  };

  return (
    <div
      className={classNames(styles.component, className, {
        [styles.isScrolled]: isScrolled,
      })}
    >
      <div className={styles.header}>
        <div
          className={styles.navigationOffset}
          style={{
            height: isScrolled ? 0 : `${navigationOffset}px`,
          }}
        />

        <div ref={searchRef}>
          <div className={styles.contentSearchBar}>
            <ContentSearchBar isStandAlone onSearch={() => setActiveTab('')} />
          </div>

          <div className={styles.tabMenu}>
            {isFetchingContentSearch && (
              <div className={styles.loader}>
                {Array.from({ length: 3 }).map((_, loaderIndex) => (
                  <SkeletonLoader
                    key={loaderIndex}
                    borderRadius="4px"
                    height="46px"
                    width="85px"
                  />
                ))}
              </div>
            )}

            {hasTabs && (
              <TabMenu
                activeItem={{
                  type: 'id',
                  value: activeTab,
                }}
                tabs={tabs}
              />
            )}
          </div>
        </div>
      </div>

      <div className={styles.container}>
        <div
          className={styles.searchOffset}
          style={{
            height: isScrolled ? `${searchOffset + navigationOffset}px` : 0,
          }}
        />

        <div className={styles.results} id="results">
          {isFetchingContentSearch &&
            Array.from({ length: 5 }).map((_, loaderIndex) => (
              <ContentSearchCard key={loaderIndex} isLoading />
            ))}

          {hasNoResults && renderNoResults()}

          {hasBanners && (
            <div className={styles.bannerContainer}>
              {dataContentSearch.banners.map((item, itemIndex) => (
                <ContentSearchBanner
                  key={itemIndex}
                  className={styles.banner}
                  text={item.Banner}
                />
              ))}
            </div>
          )}

          {showResults &&
            formattedItems.map((item, itemIndex) => (
              <ContentSearchCard key={itemIndex} item={item} />
            ))}
        </div>
      </div>
    </div>
  );
};

ContentSearch.displayName = 'ContentSearch';

export default ContentSearch;
